import { Component, memo, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as math from "mathjs";
import {
  parseArrowKey,
  processText,
  svgToDataUrl,
} from "../../../../utils/learnUtils";
import { useMathJax } from "../../../../hooks/useMathJax";
import { useCaptionHighlight } from "../../../../hooks/useCaptionHighlight";
import { useLearnCtx } from "../.././LearnPage";
import SelectionToolbar from "../SelectionToolbar";
import IntFigComponents from "./IntFigComponents";
import IntFigTimeLine from "./IntFigTimeLine";
import "./IntFigureComp.css";

/* ── Error boundary ──
   Interactive figures are built from a lot of user/editor-authored fields
   (functions, JSON, indices into params/derived arrays, ...); a malformed
   value anywhere in that pipeline should never take down the whole app.
   Resets whenever `item` changes identity (a real content edit clones the
   tree, giving a new object) so a fixed figure recovers automatically;
   interaction-only re-renders (slider drags, timeline ticks) keep the same
   `item` reference and won't spuriously clear a previously-shown error. */
class IntFigureErrorBoundary extends Component {
  state = { error: null, item: null };

  static getDerivedStateFromProps(props, state) {
    if (props.item !== state.item) {
      return { error: null, item: props.item };
    }
    return null;
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="ct__intfig-render-error">
          ⚠ This interactive figure could not be displayed (invalid data).
        </div>
      );
    }
    return this.props.children;
  }
}

/* ── Utility: build initial 100-slot params array ──
   dataParams comes straight from the editor as an array of raw strings, so
   coerce each entry to a finite number (falling back to 0) instead of
   letting a malformed/non-numeric entry propagate as a string. */
function initParams(dataParams) {
  const p = new Array(100).fill(0);
  if (dataParams?.length) {
    const nums = dataParams.map((v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    });
    p.splice(0, nums.length, ...nums);
  }
  return p;
}

/* ── Utility: compute derived quantities via mathjs ── */
function computeDerived(item, params, tParams, timer) {
  const expressions = item.data.derived ?? [];
  if (!expressions.length) return [];
  const scopep = params.reduce((acc, v, i) => {
    acc[`p${i}`] = v;
    return acc;
  }, {});
  const scopet = tParams.reduce((acc, v, i) => {
    acc[`tp${i}`] = v;
    return acc;
  }, {});
  const scope = { ...scopep, ...scopet, timer0: timer[0], timer1: timer[1] };
  const out = [];
  for (let i = 0; i < expressions.length; i++) {
    try {
      const scoped = out.reduce((acc, v, j) => {
        acc[`d${j}`] = v;
        return acc;
      }, {});
      out.push(math.evaluate(expressions[i], { ...scope, ...scoped }));
    } catch {
      out.push(0);
    }
  }
  return out;
}

/* ── Sliders icon for instructions row ── */
function SlidersIcon() {
  return (
    <svg
      className="ct__intfig-instr-icon"
      viewBox="0 0 16 14"
      fill="none"
      aria-hidden="true"
    >
      <line
        x1="1"
        y1="2"
        x2="15"
        y2="2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="5" cy="2" r="1.5" fill="currentColor" />
      <line
        x1="1"
        y1="7"
        x2="15"
        y2="7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="10" cy="7" r="1.5" fill="currentColor" />
      <line
        x1="1"
        y1="12"
        x2="15"
        y2="12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="6" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

/* ── Pointer arrows (shared with modal view) ── */
function PointerArrows({ activeKeys }) {
  const arrows = [...activeKeys].flatMap(parseArrowKey);
  return arrows.map(({ x, y }, i) => (
    <svg
      key={i}
      className="ct__intfig-ptr-arrow"
      style={{ left: `${x}%`, top: `${y}%` }}
      width="16"
      height="28"
      viewBox="0 0 16 28"
      aria-hidden="true"
    >
      <line
        x1="8"
        y1="0"
        x2="8"
        y2="17"
        stroke="white"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <polygon points="8,28 0,14 16,14" fill="white" />
      <line
        x1="8"
        y1="1"
        x2="8"
        y2="17"
        stroke="var(--blue-primary)"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <polygon points="8,27 2,15 14,15" fill="var(--blue-primary)" />
    </svg>
  ));
}

/* ── Interactive canvas (reused in normal + modal view) ── */
function IntFigCanvas({
  item,
  params,
  tParams,
  tParamsCum,
  derived,
  setParam,
  zoom = false,
  style,
}) {
  return (
    <div className="ct__intfig-canvas" style={style}>
      <img
        src={svgToDataUrl(item.data.image)}
        alt="Interactive figure"
        className="ct__intfig-bg"
      />
      <IntFigComponents
        item={item}
        params={params}
        tParams={tParams}
        tParamsCum={tParamsCum}
        derived={derived}
        setParam={setParam}
        zoom={zoom}
      />
    </div>
  );
}

/* ── Main component ── */
/**
 * Renders an EDCOMP_INT_FIGURE: interactive figure with sliders, movable points,
 * function curves, contour plots, and optional timeline animation.
 * @param {{ item: object, showExpand?: boolean }} props
 */
function IntFigureComp({ item, showExpand = true }) {
  const { data } = item;
  const { openSupport, openBookmarkModal } = useLearnCtx();

  /* ── Timeline config ── */
  const tlSpeed = data.timeline_min_max_speed?.[2] ?? 0;
  const tlMin = data.timeline_min_max_speed?.[0] ?? 0;
  const tlMax = data.timeline_min_max_speed?.[1] ?? 0;
  const hasTimeline = tlSpeed > 0;
  const tlMappings = (data.timeline_parameters ?? []).map((e) =>
    e.split(",").map(Number),
  );
  const tlMappingsFirst = tlMappings.map((m) => m[0]);

  /* ── State ── */
  const [params, setParamsState] = useState(() => initParams(data.params));
  const [tParams, setTParams] = useState(() => {
    const t = new Array(5).fill(0);
    t.splice(0, tlMappingsFirst.length, ...tlMappingsFirst);
    return t;
  });
  const [tParamsCum, setTParamsCum] = useState(undefined);
  const [timer, setTimer] = useState([0, 0]);
  const [derived, setDerived] = useState(() =>
    computeDerived(item, initParams(data.params), new Array(5).fill(0), [0, 0]),
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeKeys, setActiveKeys] = useState(new Set());
  const [modalCanvasStyle, setModalCanvasStyle] = useState(null);

  /* ── Derived update ── */
  useEffect(() => {
    setDerived(computeDerived(item, params, tParams, timer));
  }, [params, tParams, timer]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── setParam helper ── */
  const setParam = useCallback((paramId, value) => {
    setParamsState((prev) => {
      const next = [...prev];
      if (Array.isArray(paramId)) {
        paramId.forEach((id, i) => {
          next[parseInt(id)] = value[i];
        });
      } else {
        next[parseInt(paramId)] = value;
      }
      return next;
    });
  }, []);

  /* ── Timeline callback ── */
  const handleTimeline = useCallback(
    (time) => {
      setTimer([time, Math.round(time)]);
      const newTParams = tlMappings.map((mapping) => {
        const t = Math.min(Math.max(Math.round(time), 0), mapping.length - 1);
        return mapping[t];
      });
      setTParams((prev) => {
        const next = [...prev];
        next.splice(0, newTParams.length, ...newTParams);
        return next;
      });
      setTParamsCum(
        tlMappings.map((mapping) => {
          const t = Math.min(Math.max(Math.round(time), 0), mapping.length - 1);
          return mapping.slice(0, t);
        }),
      );
    },
    [tlMappings],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Caption processing ── */
  const captionRef = useRef(null);
  const modalCaptionRef = useRef(null);
  const rawCaption = data.text ?? "";
  const captionHtml = processText(rawCaption);

  // Two independent hook instances sharing the same item._id — highlights stored
  // once, re-applied independently after each caption's MathJax typeset.
  const inlineHL = useCaptionHighlight(captionRef, item._id);
  const modalHL = useCaptionHighlight(modalCaptionRef, item._id);

  useMathJax(captionRef, [rawCaption], inlineHL.onTypesetRef);
  useMathJax(modalCaptionRef, [rawCaption, isExpanded], modalHL.onTypesetRef);

  /* ── Caption click for pointer buttons ── */
  const handleCaptionClick = useCallback((e) => {
    const btn = e.target.closest(".ct__fig-ptr-btn");
    if (!btn) return;
    e.preventDefault();
    const key = btn.dataset.arrows;
    setActiveKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /* ── Modal caption click: support links close modal + open panel; also handles pointer buttons ── */
  const handleModalCaptionClick = useCallback(
    (e) => {
      const link = e.target.closest("[data-support-id]");
      if (link) {
        e.preventDefault();
        setIsExpanded(false);
        openSupport(link.dataset.supportId);
        return;
      }
      const btn = e.target.closest(".ct__fig-ptr-btn");
      if (!btn) return;
      e.preventDefault();
      const key = btn.dataset.arrows;
      setActiveKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [openSupport],
  );

  /* ── Update pointer button active state ── */
  useEffect(() => {
    [captionRef, modalCaptionRef].forEach((ref) => {
      if (!ref.current) return;
      ref.current.querySelectorAll(".ct__fig-ptr-btn").forEach((btn) => {
        btn.classList.toggle(
          "ct__fig-ptr-btn--active",
          activeKeys.has(btn.dataset.arrows),
        );
      });
    });
  }, [activeKeys]);

  /* ── Keyboard close modal ── */
  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIsExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) {
      setModalCanvasStyle(null);
      return;
    }
    const url = svgToDataUrl(data.image);
    const measurer = new window.Image();
    measurer.onload = () => {
      const { naturalWidth: nw, naturalHeight: nh } = measurer;
      if (!nw || !nh) return;
      const fs = parseFloat(
        getComputedStyle(document.documentElement).fontSize,
      );
      const innerPad = fs * 2.5;
      const captionMaxH = rawCaption
        ? Math.max(60, Math.round((rawCaption.length / 1000) * 250))
        : 0;
      const captionOverhead = rawCaption ? captionMaxH + fs : 0;
      const maxW = window.innerWidth * 0.85 - innerPad;
      const maxH =
        window.innerHeight * 0.85 - innerPad - captionOverhead - fs * 3;
      const scale = Math.min(maxW / nw, maxH / nh);
      setModalCanvasStyle({
        width: `${Math.round(nw * scale)}px`,
        height: `${Math.round(nh * scale)}px`,
      });
    };
    measurer.src = url;
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const figureWidth = data.width ?? "100%";
  const isFullWidth = figureWidth === "100%";

  const sharedProps = { item, params, tParams, tParamsCum, derived, setParam };

  return (
    <>
      <figure
        id={"intfig:" + item._id}
        className={`ct__intfig${isFullWidth ? " ct__intfig--full" : " ct__intfig--partial"}`}
      >
        <div className="ct__intfig-canvas-wrap">
          {/* Interactive canvas with background image and overlays */}
          <div className="ct__intfig-canvas" style={{ width: figureWidth }}>
            <img
              src={svgToDataUrl(data.image)}
              alt="Interactive figure"
              className="ct__intfig-bg"
            />
            <IntFigComponents {...sharedProps} />
            <PointerArrows activeKeys={activeKeys} />
          </div>
        </div>
        {/* Expand button — same positioning rules as FigureComp */}
        {showExpand && (
          <button
            className="ct__figure-expand"
            onClick={() => setIsExpanded(true)}
            aria-label="Expand figure"
            title="Expand figure"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 2h4M2 2v4M12 2H8M12 2v4M2 12h4M2 12V8M12 12H8M12 12V8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}

        {/* Timeline */}
        {hasTimeline && (
          <div className="ct__intfig-timeline">
            <IntFigTimeLine
              min={tlMin}
              max={tlMax}
              speed={tlSpeed}
              color={data.timeline?.color ?? "#1f76D2"}
              onTick={handleTimeline}
            />
          </div>
        )}

        {/* Caption + instructions */}
        {(rawCaption || data.instructions) && (
          <figcaption
            ref={captionRef}
            className="ct__figure-caption ct__intfig-caption"
            onClick={handleCaptionClick}
          >
            {rawCaption && (
              <span dangerouslySetInnerHTML={{ __html: captionHtml }} />
            )}
            {data.instructions && (
              <>
                {rawCaption && <br />}
                <span className="ct__intfig-instr">
                  <SlidersIcon />
                  <strong
                    dangerouslySetInnerHTML={{
                      __html: processText(data.instructions),
                    }}
                  />
                </span>
              </>
            )}
          </figcaption>
        )}

        {inlineHL.toolbarPos && (
          <SelectionToolbar
            style={inlineHL.toolbarStyle}
            onCopy={inlineHL.copySelection}
            copyDone={inlineHL.copyDone}
            onPointerDown={inlineHL.preventDeselect}
            onBookmark={() =>
              openBookmarkModal(item._id, item.title, "intfig:" + item._id)
            }
            onHighlight={inlineHL.handleHighlight}
            onRemoveHighlight={inlineHL.handleRemoveHighlight}
          />
        )}
      </figure>

      {/* Expanded modal — portalled to document.body so position:fixed is relative to the viewport,
           not the frame__panel stacking context (which has will-change:transform) */}
      {isExpanded &&
        createPortal(
          <div
            className="ct__intfig-modal-backdrop"
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsExpanded(false);
            }}
          >
            <div className="ct__intfig-modal">
              <button
                className="ct__intfig-modal-close"
                onClick={() => setIsExpanded(false)}
                aria-label="Close"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M1 1l10 10M11 1L1 11"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              {/* Canvas in modal */}
              <div
                className="ct__intfig-canvas ct__intfig-modal-canvas"
                style={modalCanvasStyle ?? { maxWidth: "85vw" }}
              >
                <img
                  src={svgToDataUrl(data.image)}
                  alt="Interactive figure"
                  className="ct__intfig-bg"
                />
                <IntFigComponents {...sharedProps} zoom />
                <PointerArrows activeKeys={activeKeys} />
              </div>

              {/* Timeline in modal */}
              {hasTimeline && (
                <div className="ct__intfig-modal-timeline">
                  <div className="ct__intfig-timeline">
                    <IntFigTimeLine
                      min={tlMin}
                      max={tlMax}
                      speed={tlSpeed}
                      color={data.timeline?.color ?? "#1f76D2"}
                      onTick={handleTimeline}
                    />
                  </div>
                </div>
              )}

              {/* Caption + instructions in modal */}
              {(rawCaption || data.instructions) && (
                <div
                  ref={modalCaptionRef}
                  className="ct__figure-caption ct__intfig-modal-caption"
                  onClick={handleModalCaptionClick}
                >
                  {rawCaption && (
                    <span dangerouslySetInnerHTML={{ __html: captionHtml }} />
                  )}
                  {data.instructions && (
                    <>
                      {rawCaption && <br />}
                      <span className="ct__intfig-instr">
                        <SlidersIcon />
                        <strong
                          dangerouslySetInnerHTML={{
                            __html: processText(data.instructions),
                          }}
                        />
                      </span>
                    </>
                  )}
                </div>
              )}

              {modalHL.toolbarPos && (
                <SelectionToolbar
                  style={modalHL.toolbarStyle}
                  onCopy={modalHL.copySelection}
                  copyDone={modalHL.copyDone}
                  onPointerDown={modalHL.preventDeselect}
                  onBookmark={() =>
                    openBookmarkModal(
                      item._id,
                      item.title,
                      "intfig:" + item._id,
                    )
                  }
                  onHighlight={modalHL.handleHighlight}
                  onRemoveHighlight={modalHL.handleRemoveHighlight}
                />
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export default memo(function IntFigureCompSafe(props) {
  return (
    <IntFigureErrorBoundary item={props.item}>
      <IntFigureComp {...props} />
    </IntFigureErrorBoundary>
  );
});
